import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import withLoading from 'hocs/withLoading';
import _ from 'lodash';

import useTableCellPendingChangeAuthors from 'hooks/useTableCellPendingChangeAuthors';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { deletePropertyAction } from 'store/loanApplications/actions';
import { selectSortedBorrowerProperties } from 'store/loanApplications/selectors';

import { normalizeReoPendingChanges } from '../../UpdateTableModal/Content/SectionContent/Reo/Reo.utils';
import {
  FINANCIAL_INFO_TABLE_NAME,
  getDataWithNotFoundMessage,
  TablePropTypes,
} from '../FinancialInfoBody.constants';
import { tableStyles } from '../FinancialInfoBody.styles';
import { getTotalPendingChangesCount } from '../FinancialInfoBody.utils';
import useNeededCountByRecord from '../hooks/useNeededCountByRecord';
import TableWrapper from '../TableWrapper';

import { getConfig } from './Reo.config';

const Reo = ({
  onToggle,
  tableName,
  borrowerId,
  isLoading,
  setIsLoading,
  onAddRecord,
  pendingChanges,
  chatTargets = null,
}) => {
  const properties = useSelector(selectSortedBorrowerProperties(borrowerId));
  const { applicationId, subtab } = useParams();
  const dispatch = useDispatch();

  const isExpanded = subtab === tableName;

  const onAddClick = useCallback(() => onAddRecord(tableName), [onAddRecord, tableName]);

  const onRemove = useCallback(
    recordId => {
      setIsLoading(true);

      dispatch(
        deletePropertyAction({
          propertyId: recordId,
          tabId: LoanApplicationTabId.REO,
          applicationId,
        }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setIsLoading],
  );

  const neededCountById = useNeededCountByRecord(tableName, properties);
  const getCellPendingChangeAuthors = useTableCellPendingChangeAuthors();

  const config = useMemo(
    () => getConfig({ isExpanded, onRemove, chatTargets, neededCountById }),
    [isExpanded, onRemove, chatTargets, neededCountById],
  );

  const formattedData = properties.length
    ? properties
    : getDataWithNotFoundMessage(config)[tableName];

  const propertyIds = useMemo(() => properties.map(({ id }) => id), [properties]);

  const customFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.PROPERTY,
    entityIds: propertyIds,
  });

  const pendingChangesCounts = calculatePendingChanges(
    _.merge(
      {},
      normalizeReoPendingChanges({
        initialPendingChanges: pendingChanges,
      }),
      customFieldsPendingChanges,
    ),
    true,
  );

  const totalPendingChangesCount = getTotalPendingChangesCount({
    pendingChangesCounts,
    data: properties,
  });

  return (
    <TableWrapper tableName={tableName} onToggle={onToggle}>
      <TableCDS
        withAddButton
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        getCellPendingChangeAuthors={getCellPendingChangeAuthors}
        isCustomDataDefinition={!properties.length}
        isLoading={isLoading}
        isPreview={!isExpanded}
        pendingChangesCount={totalPendingChangesCount}
        title={FINANCIAL_INFO_TABLE_NAME[tableName]}
        onAddClick={onAddClick}
      />
    </TableWrapper>
  );
};

Reo.propTypes = {
  ...TablePropTypes,
};

export default withLoading(Reo);
